import currency from "currency.js";
import { logger } from "../../../utils/logger";
import { useCallback } from "react";
import {
  InstrumentTypeResponse,
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { ICreateOrderBody } from "../../../interfaces/Order";
import { getAssetLogo } from "../../../utils/asset/assets";
import { floorToDecimalsString } from "../../../utils/decimalString";
import { effectiveStepPrecision } from "../../../utils/format";
import {
  ToastEnum,
  ToastStatusEnum,
  getToastTitleForInstrument,
} from "../../../utils/toast";
import { useOptionsTradeFormSetup } from "./useOptionsTradeFormSetup";
import { useOptionsTradeFormComputed } from "./useOptionsTradeFormComputed";
import { useOptionsTradeFormPosition } from "./useOptionsTradeFormPosition";

type SetupResult = ReturnType<typeof useOptionsTradeFormSetup>;
type ComputedResult = ReturnType<typeof useOptionsTradeFormComputed>;
type PositionResult = ReturnType<typeof useOptionsTradeFormPosition>;

export function useOptionsTradeFormSubmit({
  selectedInstrument,
  amount,
  orderType,
  orderSide,
  reduceOnly,
  price,
  createOrder,
  showOrderCreatedAnimation,
  mutateFarmData,
  reset,
  onClose,
  addToast,
  market,
  optionsTradeFormTranslations,
  commonFormTranslations,
  contractPriceStep,
  addErrorToast,
  apiError,
  setIsLoading,
}: SetupResult &
  ComputedResult &
  PositionResult & { onClose?: () => void }) {
  const submitOrder = useCallback(async () => {
    if (selectedInstrument && Number(amount)) {
      const order: ICreateOrderBody =
        orderType === OrderTypeResponse.Market
          ? {
              amount,
              //@ts-ignore
              side: orderSide,
              instrument: selectedInstrument.instrument_id,
              orderType: OrderTypeResponse.Market,
              stop: undefined,
              trigger: undefined,
              reduceOnly,
            }
          : {
              amount,
              side: orderSide,
              instrument: selectedInstrument.instrument_id,
              orderType: OrderTypeResponse.Limit,
              price: String(price),
              stop: undefined,
              trigger: undefined,
            };

      const toastInterval = 4000;

      try {
        setIsLoading(true);
        const response = await createOrder(order);

        if (response.order_id) {
          // Only show immediate toast if is market order.
          // Else just show order placed
          if (orderType === OrderTypeResponse.Market) {
            addToast(
              {
                type: ToastEnum.INFO,
                icon: getAssetLogo(market.asset) as string,
                header: (
                  <p>
                    {getToastTitleForInstrument(
                      InstrumentTypeResponse.Option,
                      selectedInstrument.instrument_name,
                      Number(selectedInstrument.expiry),
                      selectedInstrument.strike
                    )}
                  </p>
                ),
                subheader:
                  orderSide === SideResponse.Buy ? (
                    <span style={{ color: COLORS.up.one }}>
                      {optionsTradeFormTranslations("market_buy")}
                    </span>
                  ) : (
                    <span style={{ color: COLORS.down.one }}>
                      {optionsTradeFormTranslations("market_sell")}
                    </span>
                  ),
                stats: [
                  {
                    label:
                      orderSide === SideResponse.Buy
                        ? commonFormTranslations("buy_amount")
                        : commonFormTranslations("sell_amount"),
                    value: (
                      <span
                        style={{
                          color:
                            orderSide === SideResponse.Buy
                              ? COLORS.up.one
                              : COLORS.down.one,
                        }}
                      >
                        {/* Floored: `toFixed` rounded a fill half-up. */}
                        {floorToDecimalsString(
                          response.filled,
                          effectiveStepPrecision(
                            contractPriceStep.amount_step,
                            contractPriceStep.amount_precision
                          )
                        )}
                      </span>
                    ),
                  },
                  {
                    label: optionsTradeFormTranslations("avg_price"),
                    value: currency(response.avg_price || 0).format(),
                  },
                ],
                status: ToastStatusEnum.SUCCESS,
              },
              toastInterval
            );
          } else {
            addToast(
              {
                type: ToastEnum.INFO,
                icon: getAssetLogo(market.asset) as string,
                header: (
                  <p>
                    {getToastTitleForInstrument(
                      InstrumentTypeResponse.Option,
                      selectedInstrument.instrument_name,
                      Number(selectedInstrument.expiry),
                      selectedInstrument.strike
                    )}
                  </p>
                ),
                subheader:
                  orderSide === SideResponse.Buy ? (
                    <span style={{ color: COLORS.up.one }}>
                      {optionsTradeFormTranslations("bid_placed")}
                    </span>
                  ) : (
                    <span style={{ color: COLORS.down.one }}>
                      {optionsTradeFormTranslations("offer_placed")}
                    </span>
                  ),
                stats: [
                  {
                    label: commonFormTranslations("contracts"),
                    value: floorToDecimalsString(
                      order.amount,
                      effectiveStepPrecision(
                        contractPriceStep.amount_step,
                        contractPriceStep.amount_precision
                      )
                    ),
                  },
                  {
                    label: commonFormTranslations("limit_price"),
                    value: currency(order.price || 0).format(),
                  },
                ],
                status: ToastStatusEnum.SUCCESS,
              },
              toastInterval
            );
          }
          showOrderCreatedAnimation();
          mutateFarmData();
          // Reset form
          reset();
          onClose?.();
        }
      } catch (error: any) {
        logger.error("[MARKET_SELL_FAILED_1]",error)
        let errorTitle = "";
        if (orderType === OrderTypeResponse.Market) {
          errorTitle =
            orderSide === SideResponse.Buy
              ? commonFormTranslations("market_buy_failed")
              : commonFormTranslations("market_sell_failed");
        } else {
          errorTitle =
            orderSide === SideResponse.Buy
              ? commonFormTranslations("limit_buy_failed")
              : commonFormTranslations("limit_sell_failed");
        }
        addErrorToast(
          <p>{errorTitle}</p>,
          apiError(error.message) || commonFormTranslations("place_order_again")
        );
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    selectedInstrument,
    amount,
    orderType,
    orderSide,
    reduceOnly,
    price,
    createOrder,
    showOrderCreatedAnimation,
    mutateFarmData,
    reset,
    onClose,
    addToast,
    market.asset,
    optionsTradeFormTranslations,
    commonFormTranslations,
    contractPriceStep.amount_step,
    contractPriceStep.amount_precision,
    addErrorToast,
    apiError,
  ]);

  return { submitOrder };
}
